import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { useState } from 'react'
import { useStoreActions, useStoreState } from 'store/hooks'

export const ApiKeyTab = (): JSX.Element => {
    const selectedWebsite = useStoreState((state) => state.model.selectedWebsite)
    const apiKeyGenerate = useStoreActions((actions) => actions.model.apiKeyGenerate)
    const apiKeyRevoke = useStoreActions((actions) => actions.model.apiKeyRevoke)
    const updateWebsite = useStoreActions((actions) => actions.model.updateWebsite)

    const [rotateDialogOpen, setRotateDialogOpen] = useState(false)
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)

    const currentApiKey = selectedWebsite?.apiKey ?? ''

    const handleGenerate = async () => {
        if (!selectedWebsite) return
        const newKey = await apiKeyGenerate({ websiteID: selectedWebsite.id })
        if (newKey && selectedWebsite) {
            updateWebsite({ ...selectedWebsite, apiKey: newKey })
        }
    }

    const handleRotateConfirm = async () => {
        setRotateDialogOpen(false)
        if (!selectedWebsite) return
        const newKey = await apiKeyGenerate({ websiteID: selectedWebsite.id })
        if (newKey && selectedWebsite) {
            updateWebsite({ ...selectedWebsite, apiKey: newKey })
        }
    }

    const handleRevokeConfirm = async () => {
        setRevokeDialogOpen(false)
        if (!selectedWebsite) return
        await apiKeyRevoke({ websiteID: selectedWebsite.id })
        updateWebsite({ ...selectedWebsite, apiKey: '' })
    }

    const handleCopy = () => {
        if (currentApiKey) {
            navigator.clipboard.writeText(currentApiKey)
        }
    }

    return (
        <Box display="flex" flexDirection="column" p={2} maxWidth={800} marginX={'auto'}>
            <Grid container direction={'row'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
                <Typography variant="h6">API Key</Typography>
            </Grid>

            <Typography variant="body2" color="text.secondary" mb={2}>
                Use this API key to authenticate MCP connections for this website.
            </Typography>

            <Grid container mb={2} alignItems="center">
                {currentApiKey ? (
                    <Stack direction="row" spacing={1} alignItems="center" width="100%">
                        <TextField
                            label="API Key"
                            value={currentApiKey}
                            fullWidth
                            inputProps={{ readOnly: true }}
                            variant="outlined"
                        />
                        <IconButton onClick={handleCopy} title="Copy API key">
                            <ContentCopyIcon />
                        </IconButton>
                    </Stack>
                ) : (
                    <Typography variant="body1" color="text.secondary">
                        No API key generated
                    </Typography>
                )}
            </Grid>

            <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
                {!currentApiKey && (
                    <Button variant="contained" onClick={handleGenerate}>
                        Generate
                    </Button>
                )}
                {currentApiKey && (
                    <>
                        <Button variant="outlined" onClick={() => setRotateDialogOpen(true)}>
                            Rotate
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => setRevokeDialogOpen(true)}>
                            Revoke
                        </Button>
                    </>
                )}
            </Stack>

            {/* Rotate confirmation dialog */}
            <Dialog open={rotateDialogOpen} onClose={() => setRotateDialogOpen(false)}>
                <DialogTitle>Rotate API Key</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Rotating the API key will invalidate the current key. Any MCP connections using the existing key
                        will stop working. Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRotateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleRotateConfirm} color="warning" variant="contained">
                        Rotate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke confirmation dialog */}
            <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
                <DialogTitle>Revoke API Key</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Revoking the API key will permanently delete it. All MCP connections using this key will stop
                        working immediately. Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleRevokeConfirm} color="error" variant="contained">
                        Revoke
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
